// The body the pilot is flying to: the target pick and its list, the guidance
// read against it, the pilot panel's own readout, and the park verbs a check
// drives through `scene.userData.simFlight`. Nothing here reads a frame time -
// `computePilotReadout` and `guidanceToTarget` are pulled fresh each call off
// the flight state and the registry, the way the panel itself is.
import * as THREE from 'three';
import { gameState, setFlightTargetId } from '../state/gameState';
import {
  aimAlongVelocity, flightNose, flightSpeed, flightUnrotate,
  type FlightConfig, type FlightState, type FlightVec,
} from '../sim/flight';
import type { GravityField } from '../sim/gravityField';
import { basisFromNormal, orbitalBasis, type Basis, type SimPositionHandle } from '../sim/position';
import type { BodyPlace, BodyPlaces } from '../sim/bodies';
import type { AtlasWorldHandle } from '../sim/landVerb';
import type { TrajectoryHandle } from '../sim/trajectory';
import type { WarpHandle } from '../sim/warp';
import type { Custos } from '../scene/custos';
import { bodyById } from '../world/bodies';
import { LAUNCH_KEY } from './flightField';
import type { BodyRow } from '../world/types';
import type { WorldVec } from '../world/frame';
import { gameClock } from '../world/clock';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from '../sim/constants';
import {
  brakeCue, brakeCueFromPath, burnToZeroSeconds, closestApproach, makeBrakeCue,
  makeClosestApproach, type BrakePathEnd,
} from '../sim/approach';
import {
  speedDisplayStepMps, type PilotReadout, type PilotTurnSide,
} from '../pilotPanel';
import type { CruiseDrive } from './drive';
import { TEST_SWITCHES } from '../urlFlags.ts';

// The `--target` arm's five injects, plus BRAKE's own. Read under DEV only, so
// a build carries no way to reach one and vite drops each flag's own string
// out of the bundle.
const TARGET_DOMINANT = TEST_SWITCHES
  && new URLSearchParams(location.search).get('targetdominant') === '1';
const TARGET_NOSE = TEST_SWITCHES
  && new URLSearchParams(location.search).get('targetnose') === '1';
const AIM_VELOCITY = TEST_SWITCHES
  && new URLSearchParams(location.search).get('aimvelocity') === '1';
const TARGET_HDG_OFFSET = TEST_SWITCHES
  && new URLSearchParams(location.search).get('targethdgoffset') === '1';
const TARGET_HDG_OFFSET_DEG = 5;
/** `?pilotsharedrows=1` puts the pre-#356 overload back on SPD and ALT. */
const PILOT_SHARED_ROWS = TEST_SWITCHES
  && new URLSearchParams(location.search).get('pilotsharedrows') === '1';
/** `?pilotspeedoff=1` paints SPD two display steps above the ship's own speed. */
const PILOT_SPEED_OFF = TEST_SWITCHES
  && new URLSearchParams(location.search).get('pilotspeedoff') === '1';
/** `?fieldpreadvance=1` reads the pre-`simPos.advance()` capture again. */
const FIELD_PRE_ADVANCE = TEST_SWITCHES
  && new URLSearchParams(location.search).get('fieldpreadvance') === '1';
/** `?relspdhelio=1` paints SPD REL from the composed heliocentric velocity. */
const REL_SPD_HELIO = TEST_SWITCHES
  && new URLSearchParams(location.search).get('relspdhelio') === '1';
/** `?pilotdominantground=1`: a ship on a moon reads the body out-pulling it again. */
const PILOT_DOMINANT_GROUND = TEST_SWITCHES
  && new URLSearchParams(location.search).get('pilotdominantground') === '1';
/** `?assistnocapnotice=1` keeps the panel quiet while the assist's own cap binds. */
const ASSIST_NO_CAP_NOTICE = TEST_SWITCHES
  && new URLSearchParams(location.search).get('assistnocapnotice') === '1';
/** `?targetspeedframe=1` paints SPEED TO MATCH from the frame-relative speed. */
const TARGET_SPEED_FRAME = TEST_SWITCHES
  && new URLSearchParams(location.search).get('targetspeedframe') === '1';
/** `?burnframespeed=1` prices BURN off the frame-relative speed with a target picked. */
const BURN_FRAME_SPEED = TEST_SWITCHES
  && new URLSearchParams(location.search).get('burnframespeed') === '1';
/** `?hdgwhole=1` rounds NOSE OFF COURSE to whole degrees and drops its side. */
const HDG_WHOLE = TEST_SWITCHES
  && new URLSearchParams(location.search).get('hdgwhole') === '1';
/** `?turnsidenodeadband=1` removes the hold band at the diagonal between two
 *  NOSE OFF COURSE side words. */
const TURN_SIDE_NO_DEAD_BAND = TEST_SWITCHES
  && new URLSearchParams(location.search).get('turnsidenodeadband') === '1';
/** Right at the diagonal the larger of two off-axis components can swap every
 *  frame on drift too small to see in the printed degree figure; held inside
 *  this band the printed side word does not. */
const TURN_SIDE_DIAGONAL_BAND = 0.02;
/** `?closestlinear=1` drops the gravity term out of CLOSEST. */
const CLOSEST_LINEAR = TEST_SWITCHES
  && new URLSearchParams(location.search).get('closestlinear') === '1';
/** `?burnnolate=1` never lights BURN's own cue. */
const BURN_NO_LATE = TEST_SWITCHES
  && new URLSearchParams(location.search).get('burnnolate') === '1';
/** `?burnheldorbit=1` prices BURN on an orbit the pick already holds. */
const BURN_HELD_ORBIT = TEST_SWITCHES
  && new URLSearchParams(location.search).get('burnheldorbit') === '1';
/** `?brakezero=1` aims BRAKE at rest even with a periapsis above the surface. */
const BRAKE_ZERO = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakezero') === '1';
/** `?brakeframeonly=1` puts the old gate back: BRAKE speaks only about the
 *  body the ship is already flying in the frame of. */
const BRAKE_FRAME_ONLY = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakeframeonly') === '1';
/** `?brakefarreach=1` widens BRAKE's own horizon to where nothing bars it. */
const BRAKE_FAR_REACH = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakefarreach') === '1';
/** How far the target's own parent may carry it round its orbit before the
 *  moment BRAKE is counting down to, radians. `brakeConicHolds`'s own horizon. */
const BRAKE_HORIZON_RAD = BRAKE_FAR_REACH ? 1e4 : 0.2;
/** `?brakepathwins=1` lets the drawn line's far end answer past an arrival
 *  the ship is still closing on. */
const BRAKE_PATH_WINS = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakepathwins') === '1';
/** `?brakenoquiet=1` treats no capture as secure. */
const BRAKE_NO_QUIET = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakenoquiet') === '1';
/** `?brakequietclosed=1` treats every capture as secure. */
const BRAKE_QUIET_CLOSED = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakequietclosed') === '1';
/** `?brakethrottlelit=1` drops the latch that keeps a kick from a quiet orbit
 *  from relighting BRAKE the instant the throttle opens. */
const BRAKE_THROTTLE_LIT = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakethrottlelit') === '1';
/** `?brakenopath=1` puts BRAKE back on the osculating conic alone. */
const BRAKE_NO_PATH = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakenopath') === '1';
/** `?brakeleaveoff=1` drops the leave-time reading on a closed orbit whose
 *  apoapsis is outside the hold. */
const BRAKE_LEAVE_OFF = TEST_SWITCHES
  && new URLSearchParams(location.search).get('brakeleaveoff') === '1';
/** `?hudstatic=1` freezes the pilot readouts at their first cruise reading. */
const HUD_STATIC = TEST_SWITCHES
  && new URLSearchParams(location.search).get('hudstatic') === '1';
/** `?nativetargetpicker=1` lets the press itself open the target list again. */
const NATIVE_TARGET_PICKER = TEST_SWITCHES
  && new URLSearchParams(location.search).get('nativetargetpicker') === '1';

/** Read when the registry has no velocity for a row: a body at rest. */
const BODY_WORLD_VELOCITY: FlightVec = { x: 0, y: 0, z: 0 };
/** Below this relative speed there is no direction of travel to be off. */
const TARGET_REL_SPEED_MIN_MPS = 1e-3;
/** At rest, a `parkBetween` has no velocity to solve a basis from. */
const IDENTITY_BASIS: Basis = { x: { x: 1, y: 0, z: 0 }, y: { x: 0, y: 1, z: 0 },
  z: { x: 0, y: 0, z: 1 } };

/** What the target marker reads off the guidance the pilot panel shares with it. */
export interface TargetGuidance {
  label: string;
  /**
   * Unit direction from the ship to the target, in the flight state's own
   * frame - which is the heliocentric world's axes, so the marker module takes
   * it exactly the way it takes `flight.velocity`. Null at zero range, where
   * there is no line to the target at all.
   */
  dir: FlightVec | null;
  /**
   * The surface the target carries under the ship, metres from its centre: the
   * same radius `rangeM` and `closestM` are measured from, so BRAKE IN prices
   * its periapsis against the ground DISTANCE is counting down to. The drawn
   * ground on a shape-model body, the datum sphere everywhere else. A marker's
   * own angular size comes off it too.
   */
  radiusM: number;
  /** Metres from the ship to the target's own surface, floored at 0. */
  rangeM: number;
  /** Metres a second the range is shutting; negative while it opens. */
  closingMps: number;
  /** Degrees between the direction of travel and the line to the target. Null below TARGET_REL_SPEED_MIN_MPS, where there is no direction of travel to be off. */
  velocityErrDeg: number | null;
  /** Degrees between the NOSE and the line to the target. Defined whenever a target is picked and range is nonzero - the nose exists whatever the ship is doing, so this carries no speed floor. */
  aimErrDeg: number | null;
  /** Metres from the target's surface at the closest approach ahead, floored at 0. Null when the relative state carries no conic to read. */
  closestM: number | null;
  /** Seconds to that closest approach, negative once it is behind the ship. Null alongside `closestM`. */
  closestSecs: number | null;
  /**
   * True when the relative state is a bound orbit rather than an open
   * approach - the same `eps = v^2/2 - mu/r < 0` test `closestApproach`
   * makes internally to pick its own elliptical branch, recomputed here off
   * fields this struct already carries so CLOSEST can tell a recurring low
   * point from a one-time flyby without `src/sim/approach.ts` exposing
   * the state itself.
   */
  closestClosed: boolean;
  /** Metres from the target's own CENTRE, which is the radius that conic was read at. */
  distM: number;
  /** Speed relative to the target, which is the speed that conic was read at. */
  relSpeedMps: number;
  /** The target's own GM, for a maneuver read off the same conic. */
  gm: number;
  /** Is the target also the body the ship's own frame is in? Only then are BURN's time and `closestSecs` measured against one body. */
  isFrameParent: boolean;
}

export interface ParkResult {
  ok: boolean;
  why: string | null;
  bodyId: string;
  gm: number;
  radiusM: number;
  speedMps: number;
  periodS: number;
}

export interface ParkBetweenResult {
  ok: boolean;
  why: string | null;
  fromId: string;
  towardId: string;
  fraction: number;
  world: WorldVec;
}

export interface PilotTargetLive {
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
}

export interface PilotTargetDeps {
  simPos: SimPositionHandle;
  flight: FlightState;
  gravityField: GravityField;
  ship: Custos;
  live: PilotTargetLive;
  /** The bubble's inverse, kept current by `syncBubbleFrame()`. */
  invBubble: THREE.Quaternion;
  warp: WarpHandle;
  trajectory: TrajectoryHandle;
  cruiseDrive: CruiseDrive;
  atlasWorld(): AtlasWorldHandle | null;
  syncBubbleFrame(): void;
  syncShipTransform(): void;
  adoptFlightFrame(id: string | null): void;
  /** The drawn ground under a body at a direction, metres from its centre. */
  contactRadiusM(row: BodyRow, dx: number, dy: number, dz: number): number;
  /** Is the assist on and able to act? */
  assistLive(): boolean;
  flightConfig: FlightConfig;
}

export interface PilotTarget {
  /** Pick a target, or clear it with null. Syncs the select element too. */
  setFlightTarget(id: string | null): void;
  /** Pick a row on the player's behalf (GO's destination): false, target left as it is, when the list does not offer it. */
  pickTarget(id: string): boolean;
  /** Fill the pick list, nearest first. Called on the way into an open. */
  rebuildTargetOptions(): void;
  /** Where the picked target is from here, and how the ship is moving and
   *  pointing against it. Null when nothing is picked. */
  guidanceToTarget(): TargetGuidance | null;
  /** The pilot panel's own instruments this frame, or null with nothing pulling. */
  pilotReadout(): PilotReadout | null;
  /** Which way the nose has to swing to bring an off-axis pair to zero. */
  turnSideFromOffAxis(bx: number, by: number): PilotTurnSide | null;
  /** A circular orbit about a named row at a stated altitude. */
  parkInOrbit(bodyId: string, altitudeM: number, speedFrac?: number): ParkResult;
  /** At rest, a fraction of the way from one row's centre toward another's. */
  parkBetweenBodies(fromId: string, towardId: string, fraction: number): ParkBetweenResult;
  /** A stated distance from one row's centre, moving straight out at a stated speed. */
  parkOutwardFrom(bodyId: string, distanceM: number, speedMps: number,
    dirIn?: WorldVec | null): ParkResult;
  /** The inbound leg of a named conic about a row. */
  parkApproachTo(bodyId: string, periapsisM: number, eccentricity: number,
    trueAnomalyRad: number): ParkResult;
}

export function createPilotTarget(deps: PilotTargetDeps): PilotTarget {
  const {
    simPos, flight, gravityField, ship, live, invBubble, warp, trajectory, cruiseDrive,
    atlasWorld, syncBubbleFrame, syncShipTransform, adoptFlightFrame, contactRadiusM, assistLive,
    flightConfig,
  } = deps;

  // ---------- This module's own scratch and per-frame state ----------
  const _flightQuat = new THREE.Quaternion();
  const _shipWorldVel: FlightVec = { x: 0, y: 0, z: 0 };
  const _pilotNose: FlightVec = { x: 0, y: 0, z: 0 };
  const _pilotBodyVel: FlightVec = { x: 0, y: 0, z: 0 };
  const _closest = makeClosestApproach();
  const _brake = makeBrakeCue();
  // Whether a burn was owed on this body the last frame the throttle was
  // closed - the latch that keeps a kick from a quiet orbit from relighting
  // BRAKE the instant the throttle opens (`?brakethrottlelit=1` drops it).
  let brakeOwedAtOpen = false;
  let pilotReadoutFrozen: PilotReadout | null = null;
  let _lastTurnSide: PilotTurnSide | null = null;

  // ---------- The body the pilot is flying to ----------
  // The picked row lives in `gameState().ship.flight.flightTargetId`, or null
  // for none; `setFlightTargetId` is its one mutator.
  const elPilotTarget = document.getElementById('pilot-target') as HTMLSelectElement | null;

  /**
   * The ONE accessor anything outside the ship's own frame reads. `flight.velocity`
   * is the ship's velocity relative to the body pulling hardest, which is what
   * `applyAssist` steers and what SPD reports; composing the frame parent's own
   * velocity onto it is the only place a heliocentric ship velocity exists.
   */
  function shipWorldVelocity(): FlightVec {
    const frameParentId = gravityField.frameParentId;
    const pv = frameParentId === null ? null : live.simBodies?.velocityAt(frameParentId) ?? null;
    _shipWorldVel.x = flight.velocity.x + (pv?.x ?? 0);
    _shipWorldVel.y = flight.velocity.y + (pv?.y ?? 0);
    _shipWorldVel.z = flight.velocity.z + (pv?.z ?? 0);
    return _shipWorldVel;
  }

  function setFlightTarget(id: string | null): void {
    setFlightTargetId(id);
    // The target list is every row by range, so a plan can be aimed at a moon
    // whose osculating sets no view has asked for; without them Phobos is
    // planned against a point 1,357 km along its own orbit.
    atlasWorld()?.requestBodySets(id);
    if (elPilotTarget !== null && elPilotTarget.value !== (id ?? '')) {
      elPilotTarget.value = id ?? '';
    }
  }

  /**
   * Fill the pick list, nearest first. Called on the way INTO an open and never
   * while one is open: a list that reorders under the cursor cannot be used.
   */
  function rebuildTargetOptions(): void {
    const el = elPilotTarget;
    if (el === null || live.simBodies === null) return;
    const at = simPos.world;
    const rows = live.simBodies.places
      .map((pl) => ({
        id: pl.row.id,
        name: pl.row.display_name.toUpperCase(),
        rangeM: Math.hypot(pl.world.x - at.x, pl.world.y - at.y, pl.world.z - at.z),
      }))
      .sort((a, b) => a.rangeM - b.rangeM);
    const held = gameState().ship.flight.flightTargetId;
    const none = document.createElement('option');
    none.value = '';
    none.textContent = 'NONE';
    el.replaceChildren(none);
    for (const r of rows) {
      const option = document.createElement('option');
      option.value = r.id;
      option.textContent = r.name;
      el.append(option);
    }
    el.value = held ?? '';
  }

  /** A pick made for the player: the target list's own change path, with the
   *  list rebuilt so the select shows the row. False, and nothing changed, for a
   *  row the list does not offer. */
  function pickTarget(id: string): boolean {
    if (live.simBodies === null || !live.simBodies.places.some((pl) => pl.row.id === id)) return false;
    setFlightTarget(id);
    rebuildTargetOptions();
    return true;
  }

  if (elPilotTarget !== null) {
    const el = elPilotTarget;
    el.addEventListener('pointerdown', rebuildTargetOptions);
    // The keys that open a CLOSED select. An arrow step on a closed one is not
    // an open, and must not reorder the list the step is walking.
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'F4'
        || (e.altKey && e.key.startsWith('Arrow'))) rebuildTargetOptions();
    });
    el.addEventListener('change', () => {
      setFlightTarget(el.value === '' ? null : el.value);
    });
    // Open the list on the RELEASE, not the press. Chrome puts the selected row
    // under the cursor and commits on a mouseup over a row, so a popup opened by
    // the press is still open when that same click's release arrives and eats it
    // as a pick - and where the popup flips to stay on screen decides which row
    // that is, which is why a draggable panel makes it positional. Preventing the
    // mousedown default stops the platform opening it; showPicker() from the
    // release opens it after there is no release left to spend. The release is
    // read off mouseup rather than click because no click ever reaches this
    // element - the row rebuild on pointerdown replaces the option list between
    // the press and the release. Keyboard opens (Enter, Space, F4, Alt+Arrow)
    // never went through mousedown and are untouched.
    if (!NATIVE_TARGET_PICKER && typeof el.showPicker === 'function') {
      let pressedHere = false;
      el.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        pressedHere = true;
        el.focus();
      });
      el.addEventListener('mouseup', (e) => {
        if (e.button !== 0 || !pressedHere) return;
        pressedHere = false;
        try {
          el.showPicker();
        } catch {
          // A browser that refuses the call still has the list; the press just
          // no longer opens it.
        }
      });
    }
  }

  /**
   * Where the picked target is from here, and how the ship is moving and
   * pointing against it. Null when nothing is picked.
   *
   * Relative velocity throughout, since the registry's bodies are not fixed
   * points and are not at rest either: a ship matching a body's own
   * orbit reads 0 closing and no velocity error at all, which is the correct
   * reading and not a gap. CLOSING, VELOCITY ERR and AIM ERR therefore mean
   * relative to a MOVING body, and the maneuver they describe changes shape with
   * them, from brake-to-circular to match velocity and then circularize. VELOCITY ERR is
   * measured from the VELOCITY and AIM ERR from the NOSE - flight assist (PR
   * #324) makes the nose the control input that drags the velocity around, so
   * turning to zero AIM ERR is what walks VELOCITY ERR down to meet it.
   */
  function guidanceToTarget(): TargetGuidance | null {
    const flightTargetId = gameState().ship.flight.flightTargetId;
    if (flightTargetId === null) return null;
    const row = bodyById(live.bodyRows, flightTargetId);
    const at = live.simBodies?.worldAt(flightTargetId) ?? null;
    if (row === undefined || at === null) {
      // The row left the registry, so the pick cannot mean anything any more.
      setFlightTarget(null);
      return null;
    }
    const label = row.display_name.toUpperCase();
    // The ship's position AS DRAWN this frame - `simPos.world`, after this
    // frame's own `simPos.advance()` - not `gravityField.field.at`, which
    // `updateFlightField()` set before it. `?fieldpreadvance=1` puts the stale
    // capture back on purpose.
    const shipAt = FIELD_PRE_ADVANCE ? gravityField.field.at : simPos.world;
    const dx = at.x - shipAt.x;
    const dy = at.y - shipAt.y;
    const dz = at.z - shipAt.z;
    const distM = Math.hypot(dx, dy, dz);
    const isFrameParent = gravityField.frameParentId === flightTargetId;
    if (!(distM > 0)) {
      // No line to the target exists at zero range, so neither angle does.
      return { label, dir: null, radiusM: row.radius_m, rangeM: 0, closingMps: 0,
        velocityErrDeg: null, aimErrDeg: null, closestM: null, closestSecs: null, closestClosed: false,
        distM: 0, relSpeedMps: 0, gm: 0, isFrameParent };
    }
    const ux = dx / distM;
    const uy = dy / distM;
    const uz = dz / distM;
    // Both sides in the world the registry is drawn in: the ship's own frame is
    // the body PULLING hardest, which is not in general the body being aimed at.
    const v = shipWorldVelocity();
    const bv = live.simBodies?.velocityAt(flightTargetId) ?? BODY_WORLD_VELOCITY;
    const rvx = v.x - bv.x;
    const rvy = v.y - bv.y;
    const rvz = v.z - bv.z;
    const relSpeedMps = Math.hypot(rvx, rvy, rvz);
    const closingMps = rvx * ux + rvy * uy + rvz * uz;
    const nose = flightNose(flight, _pilotNose);
    const noseDot = nose.x * ux + nose.y * uy + nose.z * uz;
    let aimErrDeg = (Math.acos(Math.min(1, Math.max(-1, noseDot))) * 180) / Math.PI;
    if (AIM_VELOCITY && relSpeedMps > TARGET_REL_SPEED_MIN_MPS) {
      const rvDot = (rvx * ux + rvy * uy + rvz * uz) / relSpeedMps;
      aimErrDeg = (Math.acos(Math.min(1, Math.max(-1, rvDot))) * 180) / Math.PI;
    }
    let velocityErrDeg: number | null = null;
    if (TARGET_NOSE) {
      velocityErrDeg = (Math.acos(Math.min(1, Math.max(-1, noseDot))) * 180) / Math.PI;
    } else if (relSpeedMps > TARGET_REL_SPEED_MIN_MPS) {
      velocityErrDeg = (Math.acos(Math.min(1, Math.max(-1, closingMps / relSpeedMps))) * 180) / Math.PI;
    }
    // The conic is taken in the TARGET's own frame, which is what divides that
    // body's heliocentric travel back out of both ends of the state.
    const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * row.mass_kg;
    closestApproach(-dx, -dy, -dz, rvx, rvy, rvz, gm, CLOSEST_LINEAR, _closest);
    // Same test as `closestApproach`'s own elliptical/hyperbolic split, off the
    // inputs already handed to it: negative specific energy is a bound orbit.
    const closestClosed = (relSpeedMps * relSpeedMps) / 2 - gm / distM < 0;
    // What DISTANCE and CLOSEST are measured from: the drawn ground under the
    // ship on a shape-model body, whose surface stands kilometres either side of
    // `radius_m`, so both rows and the stop agree. CLOSEST carries no direction
    // of its own - the conic gives a radius and a time - so the ground under the
    // ship now is what its own approach is priced against.
    const surfaceM = contactRadiusM(row, -dx, -dy, -dz);
    return { label, dir: { x: ux, y: uy, z: uz }, radiusM: surfaceM,
      rangeM: Math.max(0, distM - surfaceM), closingMps, velocityErrDeg, aimErrDeg,
      closestM: _closest.ok ? Math.max(0, _closest.radiusM - surfaceM) : null,
      closestSecs: _closest.ok ? _closest.timeS : null, closestClosed,
      distM, relSpeedMps, gm, isFrameParent };
  }

  /**
   * The drawn prediction's far end when it is about the body BRAKE is cueing:
   * the state its own caption is printing, and that caption's own seconds. Null
   * when there is nothing to read it off - the line off, nothing built yet, a
   * forecast that ran out of horizon without deciding, or one about a body the
   * pilot has not picked. `src/sim/trajectory.ts` owns both.
   */
  function brakePathEnd(bodyId: string | null): { reason: BrakePathEnd; atS: number } | null {
    if (BRAKE_NO_PATH || bodyId === null || !trajectory.enabled) return null;
    const built = trajectory.built();
    if (built === null || built.end.reason === 'budget') return null;
    if (built.end.bodyId !== bodyId) return null;
    return { reason: built.end.reason, atS: built.end.atS };
  }

  /**
   * Is the conic `closestApproach` just read about the pick the path the ship is
   * actually on? Two terms, and the ship's own frame is a third that subsumes
   * both, since inside it nothing out-pulls the pick at all.
   *
   * The path has to ARRIVE - come inside the radius the pick holds a ship in -
   * or there is no orbit at the far end to owe a burn for, however near the ship
   * happens to be right now. And the pick's parent must not have rewritten the
   * conic before the moment counted down to, which `BRAKE_HORIZON_RAD` bars.
   *
   * Distance to the pick is deliberately not a term. An interplanetary leg at
   * 100 km/s owes about 1e10 m of stopping distance, forty Earth hold radii, so
   * any bar on range tight enough to mean something opens the row with the burn
   * already overdue - the complaint this rule exists to answer.
   */
  function brakeConicHolds(bodyId: string, holdRadiusM: number,
    closestM: number, closestS: number): boolean {
    if (!(holdRadiusM > 0) || !(closestM <= holdRadiusM)) return false;
    // The frame parent is the innermost body whose hold the ship is inside, so
    // any third one there is a pull the target-relative conic cannot see.
    const parentId = bodyById(live.bodyRows, bodyId)?.parent ?? null;
    const frameParentId = gravityField.frameParentId;
    if (frameParentId !== null && frameParentId !== bodyId && frameParentId !== parentId) return false;
    const n = gravityField.meanMotionRadS(bodyId);
    return !(n > 0) || n * Math.abs(closestS) <= BRAKE_HORIZON_RAD;
  }

  /**
   * Which way the nose has to swing to bring NOSE OFF COURSE down: the side the
   * direction of travel sits on IN THE SHIP'S OWN FRAME, which is the side the
   * prograde marker is drawn on with the camera behind the bow. Nose is -Z,
   * right +X, up +Y, so the off-axis pair is what names the side and the larger
   * of the two names it alone. Null dead ahead and dead astern, where no side is
   * nearer than another and the marker has none to sit on either.
   */
  function turnSideFromOffAxis(bx: number, by: number): PilotTurnSide | null {
    const ax = Math.abs(bx);
    const ay = Math.abs(by);
    const off = Math.hypot(ax, ay);
    if (off <= 0) { _lastTurnSide = null; return null; }
    const onDiagonal = !TURN_SIDE_NO_DEAD_BAND
      && Math.abs(ax - ay) < off * TURN_SIDE_DIAGONAL_BAND;
    const side = onDiagonal && _lastTurnSide !== null
      ? _lastTurnSide
      : (ax >= ay ? (bx > 0 ? 'RIGHT' : 'LEFT') : (by > 0 ? 'UP' : 'DOWN'));
    _lastTurnSide = side;
    return side;
  }
  function turnSideToVelocity(v: FlightVec): PilotTurnSide | null {
    const b = flightUnrotate(flight, v, _pilotBodyVel);
    return turnSideFromOffAxis(b.x, b.y);
  }

  function computePilotReadout(): PilotReadout | null {
    const flightTargetId = gameState().ship.flight.flightTargetId;
    const shipAt = FIELD_PRE_ADVANCE ? gravityField.field.at : simPos.world;
    const dominantIdx = gravityField.pickDominant(shipAt);
    // A ship resting on a moon reads against the moon: on Phobos, Mars still
    // out-pulls it, and HEIGHT over Mars was 6,000 km with the hull on the ground.
    const groundIdx = flight.grounded === null || PILOT_DOMINANT_GROUND
      ? -1 : gravityField.pullIds.indexOf(flight.grounded);
    const onMoon = groundIdx >= 0 && dominantIdx >= 0 && groundIdx !== dominantIdx;
    const bestIdx = onMoon ? groundIdx : dominantIdx;
    if (bestIdx < 0) return null;
    const pull = gravityField.pulls[bestIdx]!;
    const bodyId = gravityField.pullIds[bestIdx] ?? '';
    const row = bodyById(live.bodyRows, bodyId);
    const distM = Math.hypot(
      pull.at.x - shipAt.x, pull.at.y - shipAt.y, pull.at.z - shipAt.z);
    // The ground under the ship, not the datum sphere: on a shape-model body the
    // two stand kilometres apart, and this row has to read zero where the sweep
    // stops the ship and the seat puts it down.
    const surfaceM = row === undefined ? 0 : contactRadiusM(
      row, shipAt.x - pull.at.x, shipAt.y - pull.at.y, shipAt.z - pull.at.z);
    const altitudeM = Math.max(0, distM - surfaceM);
    // SPD REL is the speed relative to the body this panel names, which is what
    // `flight.velocity` has been since the ship started integrating in that
    // body's frame. `?relspdhelio=1` paints the composed heliocentric one.
    const relV = REL_SPD_HELIO ? shipWorldVelocity() : flight.velocity;
    let speedMps = REL_SPD_HELIO
      ? Math.hypot(relV.x, relV.y, relV.z) : flightSpeed(flight);
    // Resting on a moon, the flight frame is still the planet's, so the frame's
    // own speed is the moon's orbit; nothing on the ground is travelling.
    const trackSpeedMps = onMoon ? 0 : flightSpeed(flight);
    if (onMoon) {
      const v = shipWorldVelocity();
      const bv = live.simBodies?.velocityAt(bodyId) ?? BODY_WORLD_VELOCITY;
      speedMps = Math.hypot(v.x - bv.x, v.y - bv.y, v.z - bv.z);
    }
    let headingOffDeg: number | null = null;
    let headingOffSide: PilotTurnSide | null = null;
    if (trackSpeedMps > 0) {
      const nose = flightNose(flight, _pilotNose);
      const v = flight.velocity;
      const dot = (nose.x * v.x + nose.y * v.y + nose.z * v.z) / trackSpeedMps;
      headingOffDeg = (Math.acos(Math.min(1, Math.max(-1, dot))) * 180) / Math.PI;
      headingOffSide = turnSideToVelocity(v);
    }
    // The title keeps naming the dominant pull either way; SPEED, HEIGHT and
    // NOSE OFF COURSE stay about it too - the last used to swap to COURSE OFF
    // TARGET instead of adding it beside, which took the off-prograde figure
    // away the moment a target was picked. A pick ADDS the guidance rows rather
    // than swapping any of the three out; only BURN changes what it prices.
    const guide = guidanceToTarget();
    const guided = guide !== null && !TARGET_DOMINANT;
    // The speed a rendezvous has to null, which is not the one the row above
    // carries: station-keeping off a moon of the body in the title, SPEED is the
    // whole orbital speed about that body and this is near zero.
    const targetSpeedMps = guided && guide !== null
      ? (TARGET_SPEED_FRAME ? speedMps : guide.relSpeedMps) : null;
    // BURN prices the speed the pilot is about to cancel, which with a target
    // picked is the target-relative one. The accel is the flight config's, the
    // same one the step spends, never a second copy of the canon figure.
    const burnSpeedMps = targetSpeedMps !== null && !BURN_FRAME_SPEED ? targetSpeedMps : speedMps;
    const closestSecs = guided ? guide.closestSecs : null;
    // BRAKE is the WHEN to the retrograde glyph's WHERE, and every term it needs
    // is about the TARGET, not about the frame: the gate asks whether the conic
    // about the pick is the path the ship is on, not whether the pick happens to
    // be out-pulling everything else yet. LATE keeps the frame test below, where
    // it belongs - its burn is measured against the frame parent's own speed.
    const brakeHoldM = guide === null ? 0 : gravityField.holdRadiusM(flightTargetId ?? '');
    const brakeReaches = guide !== null && (guide.isFrameParent
      || brakeConicHolds(flightTargetId ?? '', brakeHoldM, _closest.radiusM, _closest.timeS));
    const braking = guided && guide !== null && _closest.ok
      && (BRAKE_FRAME_ONLY ? guide.isFrameParent : brakeReaches);
    if (braking && guide !== null) {
      // The conic first: the drawn line below needs to know whether an arrival is
      // ahead of its own far end. A throttle open on a burn that was already owed
      // passes 0 for the hold, so the row keeps its cut target instead of going
      // blank the instant the conic closes; a kick from a quiet orbit must not.
      const thrusting = cruiseDrive.throttleApplied !== 0;
      const holdM = BRAKE_QUIET_CLOSED ? Infinity
        : BRAKE_NO_QUIET || (thrusting && (brakeOwedAtOpen || BRAKE_THROTTLE_LIT)) ? 0
          : brakeHoldM;
      brakeCue(guide.distM, guide.relSpeedMps, _closest.radiusM, _closest.timeS,
        guide.gm, guide.radiusM, holdM, flightConfig.accel, flightConfig.turnRate,
        BRAKE_ZERO || CLOSEST_LINEAR, BRAKE_LEAVE_OFF, _brake);
      // An arrival the ship is still closing on: the conic has sized a burn for
      // it, which the leave branch and the quiet both leave at zero.
      const arriving = _brake.ok && _brake.burnS > 0 && guide.closingMps > 0;
      const path = brakePathEnd(flightTargetId);
      // The drawn line's far end answers over the conic, because a caption saying
      // LEAVES or GROUND beside a blank row is the row being wrong: while the
      // throttle is open that forecast flies the burn on and the conic cannot see
      // a burn that has not happened yet. One exception - a line that LEAVES past
      // an arrival still ahead is reading the exit after the flyby, and the burn
      // the pilot owes is the arrival, which comes first.
      if (path !== null && (BRAKE_PATH_WINS || !(path.reason === 'left' && arriving))) {
        brakeCueFromPath(path.reason, path.atS, guide.relSpeedMps,
          flightConfig.accel, flightConfig.turnRate, _brake);
      }
      if (!thrusting) brakeOwedAtOpen = _brake.ok && _brake.burnS > 0;
    } else {
      _brake.ok = false;
      brakeOwedAtOpen = false;
    }
    // Parked on an orbit the pick holds, the orbit BRAKE goes quiet for, no burn
    // is owed to reach it: stopping dead against it is no plan's next burn.
    const held = braking && _brake.held && !BURN_HELD_ORBIT;
    const burnSecs = held ? null : burnToZeroSeconds(burnSpeedMps, flightConfig.accel);
    return {
      bodyLabel: (row?.display_name ?? bodyId).toUpperCase(),
      // `?pilotsharedrows=1` is the pre-#356 control: SPD/ALT read the guided
      // values again, the way one shared row used to.
      // Sized off the pre-offset speed, so a truth within two steps below 1000 m/s paints into the km/s row, where the harness faults rather than passes.
      speedMps: PILOT_SHARED_ROWS && guided ? guide.closingMps
        : PILOT_SPEED_OFF ? speedMps + 2 * speedDisplayStepMps(speedMps)
          : speedMps,
      altitudeM: PILOT_SHARED_ROWS && guided ? guide.rangeM : altitudeM,
      headingOffDeg: HDG_WHOLE && headingOffDeg !== null ? Math.round(headingOffDeg)
        : TARGET_HDG_OFFSET && headingOffDeg !== null ? headingOffDeg + TARGET_HDG_OFFSET_DEG
          : headingOffDeg,
      headingOffSide: HDG_WHOLE ? null : headingOffSide,
      targetSpeedMps,
      closingMps: guided ? guide.closingMps : null,
      rangeM: guided ? guide.rangeM : null,
      offTargetDeg: guided ? guide.velocityErrDeg : null,
      aimErrDeg: guided ? guide.aimErrDeg : null,
      burnSecs,
      // The cue compares two times, so it goes quiet whenever they are not
      // measured against the same body - the frame test below is what says BURN
      // and CLOSEST are about one body rather than two.
      burnLate: !BURN_NO_LATE && burnSecs !== null && closestSecs !== null
        && closestSecs > 0 && guided && guide.isFrameParent && burnSecs > closestSecs,
      closestM: guided ? guide.closestM : null,
      closestSecs,
      closestClosed: guided ? guide.closestClosed : false,
      brakeSecs: _brake.ok ? _brake.startS : null,
      brakeAmber: _brake.ok && _brake.startS <= _brake.marginS,
      brakeApprox: _brake.ok && _brake.approx,
      deltaVMps: flight.deltaVSpent,
      warpFactor: warp.measuredRate,
      warpAuto: warp.autoWarpDriving,
      targetLabel: guide === null ? null : guide.label,
      groundedLabel: flight.grounded === null ? null
        : (bodyById(live.bodyRows, flight.grounded)?.display_name ?? flight.grounded).toUpperCase(),
      launchKey: LAUNCH_KEY,
      assistCapped: !ASSIST_NO_CAP_NOTICE && assistLive() && flight.assistCapped,
    };
  }

  /**
   * `?hudstatic=1` locks this in at the first non-null reading and hands every
   * later frame the same object back, unread and untouched by whatever the ship
   * actually does next.
   */
  function pilotReadout(): PilotReadout | null {
    const fresh = computePilotReadout();
    if (!HUD_STATIC) return fresh;
    if (pilotReadoutFrozen === null && fresh !== null) pilotReadoutFrozen = fresh;
    return pilotReadoutFrozen;
  }

  /**
   * Put the ship on a circular orbit about one registry row, at an altitude
   * above that row's own `radius_m`, with the speed the row's mass gives. Only
   * `scene.userData.simFlight.park` reaches it; nothing a player presses does.
   *
   * The plane is chosen NORMAL to the body's own sunward direction. The ship
   * flies in the frame of the body pulling hardest and that frame's own fall is
   * subtracted from the field, so the primary's pull on a satellite of it is a
   * constant force rather than a tide. Normal to the plane that force is a
   * bounded out-of-plane offset of g/omega^2 - 3 km over Mars at 300 km, against
   * a 3,696 km radius - where in the plane it would build eccentricity orbit by
   * orbit instead.
   */
  function parkInOrbit(bodyId: string, altitudeM: number, speedFrac = 1): ParkResult {
    const no = (why: string): ParkResult => (
      { ok: false, why, bodyId, gm: 0, radiusM: 0, speedMps: 0, periodS: 0 });
    if (live.simBodies === null) return no('the body registry has not landed yet');
    const place = live.simBodies.places.find((pl) => pl.row.id === bodyId);
    if (place === undefined) return no(`the registry has no row "${bodyId}"`);
    if (place.row.parent === null) {
      return no(`"${bodyId}" is the primary, which has no sunward direction to take a plane from`);
    }
    const primary = live.simBodies.places.find((pl) => pl.row.parent === null);
    if (primary === undefined) return no('the registry has no parentless row to take a plane from');
    const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
    if (!(gm > 0)) return no(`"${bodyId}" carries no mass to orbit`);
    const radiusM = place.row.radius_m + altitudeM;
    if (!(radiusM > 0)) return no(`altitude ${altitudeM} m is inside "${bodyId}"`);
    // The orbit normal: the body's own direction from the primary.
    const nx = place.world.x - primary.world.x;
    const ny = place.world.y - primary.world.y;
    const nz = place.world.z - primary.world.z;
    const nl = Math.hypot(nx, ny, nz);
    if (!(nl > 0)) return no(`"${bodyId}" sits on the primary, so it has no sunward direction`);
    const n = new THREE.Vector3(nx / nl, ny / nl, nz / nl);
    // Any unit vector across the normal: the smallest component of it makes the
    // most stable cross product.
    const axis = Math.abs(n.x) <= Math.abs(n.y) && Math.abs(n.x) <= Math.abs(n.z)
      ? new THREE.Vector3(1, 0, 0)
      : (Math.abs(n.y) <= Math.abs(n.z) ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1));
    const rHat = axis.clone().cross(n).normalize();
    const vHat = n.clone().cross(rHat).normalize();
    const circularMps = Math.sqrt(gm / radiusM);
    // Below 1 the track is an arc that falls short of the horizon, which is the
    // state the predicted path's own ground reading is checked against.
    const speedMps = circularMps * speedFrac;
    const periodS = (2 * Math.PI * radiusM) / circularMps;
    const local = { x: rHat.x * radiusM, y: rHat.y * radiusM, z: rHat.z * radiusM };
    const world: WorldVec = {
      x: place.world.x + local.x, y: place.world.y + local.y, z: place.world.z + local.z };
    const velocity = { x: vHat.x * speedMps, y: vHat.y * speedMps, z: vHat.z * speedMps };
    simPos.seedAt(world, orbitalBasis(local, velocity));
    syncBubbleFrame();
    syncShipTransform();
    flight.velocity.x = velocity.x;
    flight.velocity.y = velocity.y;
    flight.velocity.z = velocity.z;
    // A park writes the velocity in the frame of the body it parked about, so
    // the handoff has nothing to reconcile the next time the field is read.
    adoptFlightFrame(bodyId);
    // Nose prograde, so the chase camera looks along the track rather than at
    // whatever the boot seed left it pointing at. This is an aim and not a
    // framing pose, so the assist has one to hold from here - along the velocity
    // the park just gave the ship, which costs nothing to hold at this instant.
    aimAlongVelocity(flight);
    _flightQuat.setFromUnitVectors(new THREE.Vector3(0, 0, -1), vHat);
    flight.orientation.x = _flightQuat.x;
    flight.orientation.y = _flightQuat.y;
    flight.orientation.z = _flightQuat.z;
    flight.orientation.w = _flightQuat.w;
    ship.group.quaternion.copy(invBubble).multiply(_flightQuat);
    return { ok: true, why: null, bodyId, gm, radiusM, speedMps, periodS };
  }

  /**
   * Put the ship at rest on the line between two registry rows' own centres, a
   * fraction of the way from `fromId` toward `towardId`. Only
   * `scene.userData.simFlight.parkBetween` reaches it; nothing a player presses
   * does. Built for `--flight --pulls`'s motion reading, which needs the ship
   * held still where more than one body's pull matters rather than in a single
   * body's own orbit. Orientation is arbitrary - the ship is not moving - so it
   * reuses the world axes rather than solving a basis from a zero velocity.
   */
  function parkBetweenBodies(fromId: string, towardId: string, fraction: number): ParkBetweenResult {
    const no = (why: string): ParkBetweenResult => (
      { ok: false, why, fromId, towardId, fraction, world: { x: 0, y: 0, z: 0 } });
    if (live.simBodies === null) return no('the body registry has not landed yet');
    const from = live.simBodies.places.find((pl) => pl.row.id === fromId);
    if (from === undefined) return no(`the registry has no row "${fromId}"`);
    const toward = live.simBodies.places.find((pl) => pl.row.id === towardId);
    if (toward === undefined) return no(`the registry has no row "${towardId}"`);
    const world: WorldVec = {
      x: from.world.x + (toward.world.x - from.world.x) * fraction,
      y: from.world.y + (toward.world.y - from.world.y) * fraction,
      z: from.world.z + (toward.world.z - from.world.z) * fraction,
    };
    simPos.seedAt(world, IDENTITY_BASIS);
    syncBubbleFrame();
    syncShipTransform();
    flight.velocity.x = 0;
    flight.velocity.y = 0;
    flight.velocity.z = 0;
    adoptFlightFrame(null);
    flight.assistAim = null;
    ship.group.quaternion.copy(invBubble);
    return { ok: true, why: null, fromId, towardId, fraction, world };
  }

  /**
   * The plane an outward park is seeded on: the row's own orbital normal about
   * the primary, which is the plane OWNER DECISION 1 puts a fight near that row
   * on, and which the outward line lies in. Falls back to a stable perpendicular
   * of that line when the registry carries no velocity yet, so the answer is
   * deterministic either way rather than left to rounding.
   */
  function outwardPlaneNormal(place: BodyPlace, primary: BodyPlace, out: THREE.Vector3): WorldVec {
    const vx = place.velocity.x - primary.velocity.x;
    const vy = place.velocity.y - primary.velocity.y;
    const vz = place.velocity.z - primary.velocity.z;
    const px = out.y * vz - out.z * vy;
    const py = out.z * vx - out.x * vz;
    const pz = out.x * vy - out.y * vx;
    if (Math.hypot(px, py, pz) > 0) return { x: px, y: py, z: pz };
    const axis = Math.abs(out.x) <= Math.abs(out.y) && Math.abs(out.x) <= Math.abs(out.z)
      ? new THREE.Vector3(1, 0, 0)
      : (Math.abs(out.y) <= Math.abs(out.z)
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(0, 0, 1));
    const f = axis.cross(out);
    return { x: f.x, y: f.y, z: f.z };
  }

  /**
   * Put the ship a stated distance from one row's centre, on the side away from
   * the primary, moving straight out from that row at a stated speed. Only
   * `scene.userData.simFlight.parkOutward` reaches it; nothing a player presses
   * does. Built for the predicted path's own dominant-pull crossing, which needs
   * the ship where one body stops pulling hardest and another starts - a place
   * 133,000 km off Mars that no circular park reaches.
   */
  function parkOutwardFrom(bodyId: string, distanceM: number, speedMps: number,
    dirIn: WorldVec | null = null): ParkResult {
    const no = (why: string): ParkResult => (
      { ok: false, why, bodyId, gm: 0, radiusM: 0, speedMps: 0, periodS: 0 });
    if (live.simBodies === null) return no('the body registry has not landed yet');
    const place = live.simBodies.places.find((pl) => pl.row.id === bodyId);
    if (place === undefined) return no(`the registry has no row "${bodyId}"`);
    const primary = live.simBodies.places.find((pl) => pl.row.parent === null);
    if (primary === undefined) return no('the registry has no parentless row to take a line from');
    const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
    if (!(gm > 0)) return no(`"${bodyId}" carries no mass to leave`);
    if (!(distanceM > 0)) return no(`distance ${distanceM} m is not outside "${bodyId}"`);
    const nx = dirIn?.x ?? place.world.x - primary.world.x;
    const ny = dirIn?.y ?? place.world.y - primary.world.y;
    const nz = dirIn?.z ?? place.world.z - primary.world.z;
    const nl = Math.hypot(nx, ny, nz);
    if (!(nl > 0)) {
      return no(dirIn === null
        ? `"${bodyId}" sits on the primary, so it has no line to leave along`
        : 'the line handed in has no length');
    }
    const out = new THREE.Vector3(nx / nl, ny / nl, nz / nl);
    const local = { x: out.x * distanceM, y: out.y * distanceM, z: out.z * distanceM };
    const world: WorldVec = {
      x: place.world.x + local.x, y: place.world.y + local.y, z: place.world.z + local.z };
    const velocity = { x: out.x * speedMps, y: out.y * speedMps, z: out.z * speedMps };
    // Position and velocity here are scaled copies of one radial direction, so
    // their cross product is zero in exact arithmetic and float noise in
    // doubles: the plane is named rather than solved from the state.
    simPos.seedAt(world, warp.parkRadialBasis
      ? orbitalBasis(local, velocity)
      : basisFromNormal(outwardPlaneNormal(place, primary, out), out));
    syncBubbleFrame();
    syncShipTransform();
    flight.velocity.x = velocity.x;
    flight.velocity.y = velocity.y;
    flight.velocity.z = velocity.z;
    adoptFlightFrame(bodyId);
    aimAlongVelocity(flight);
    _flightQuat.setFromUnitVectors(new THREE.Vector3(0, 0, -1), out);
    flight.orientation.x = _flightQuat.x;
    flight.orientation.y = _flightQuat.y;
    flight.orientation.z = _flightQuat.z;
    flight.orientation.w = _flightQuat.w;
    ship.group.quaternion.copy(invBubble).multiply(_flightQuat);
    return { ok: true, why: null, bodyId, gm, radiusM: distanceM, speedMps, periodS: 0 };
  }

  /**
   * Put the ship on the INBOUND leg of a named conic about one registry row: a
   * periapsis radius, an eccentricity, and a true anomaly, which is negative on
   * the way in. Only `scene.userData.simFlight.parkApproach` reaches it; nothing
   * a player presses does.
   *
   * The two parks above cannot make an approach whose time to periapsis is the
   * same size as a brake burn - `parkInOrbit` starts at an apsis, whose half
   * period about Earth is never under about 2,500 s, and `parkOutwardFrom` is
   * radial, so its periapsis is the centre. The plane is the row's own orbital
   * normal about the primary, the same one an outward park is seeded on.
   */
  function parkApproachTo(
    bodyId: string, periapsisM: number, eccentricity: number, trueAnomalyRad: number,
  ): ParkResult {
    const no = (why: string): ParkResult => (
      { ok: false, why, bodyId, gm: 0, radiusM: 0, speedMps: 0, periodS: 0 });
    if (live.simBodies === null) return no('the body registry has not landed yet');
    const place = live.simBodies.places.find((pl) => pl.row.id === bodyId);
    if (place === undefined) return no(`the registry has no row "${bodyId}"`);
    const primary = live.simBodies.places.find((pl) => pl.row.parent === null);
    if (primary === undefined) return no('the registry has no parentless row to take a plane from');
    const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
    if (!(gm > 0)) return no(`"${bodyId}" carries no mass to approach`);
    if (!(periapsisM > 0)) return no(`periapsis ${periapsisM} m is not outside "${bodyId}"'s centre`);
    if (!(eccentricity >= 0) || eccentricity === 1) {
      return no(`eccentricity ${eccentricity} has no conic (1 is the parabolic knife edge)`);
    }
    const semiLatus = periapsisM * (1 + eccentricity);
    const distM = semiLatus / (1 + eccentricity * Math.cos(trueAnomalyRad));
    if (!(distM > 0)) return no(`true anomaly ${trueAnomalyRad} rad is past this conic's asymptote`);
    const nx = place.world.x - primary.world.x;
    const ny = place.world.y - primary.world.y;
    const nz = place.world.z - primary.world.z;
    const nl = Math.hypot(nx, ny, nz);
    if (!(nl > 0)) return no(`"${bodyId}" sits on the primary, so it has no line to take a plane from`);
    const out = new THREE.Vector3(nx / nl, ny / nl, nz / nl);
    const normal = outwardPlaneNormal(place, primary, out);
    const nHat = new THREE.Vector3(normal.x, normal.y, normal.z).normalize();
    // The periapsis direction is the outward line, and the in-plane axis across
    // it carries the true anomaly; `out x nHat` closes a right-handed pair.
    const across = nHat.clone().cross(out).normalize();
    const c = Math.cos(trueAnomalyRad);
    const s = Math.sin(trueAnomalyRad);
    const k = Math.sqrt(gm / semiLatus);
    const radialMps = k * eccentricity * s;
    const alongMps = k * (1 + eccentricity * c);
    const rHat = new THREE.Vector3(out.x * c + across.x * s,
      out.y * c + across.y * s, out.z * c + across.z * s);
    const tHat = new THREE.Vector3(-out.x * s + across.x * c,
      -out.y * s + across.y * c, -out.z * s + across.z * c);
    const local = { x: rHat.x * distM, y: rHat.y * distM, z: rHat.z * distM };
    const world: WorldVec = {
      x: place.world.x + local.x, y: place.world.y + local.y, z: place.world.z + local.z };
    const velocity = {
      x: rHat.x * radialMps + tHat.x * alongMps,
      y: rHat.y * radialMps + tHat.y * alongMps,
      z: rHat.z * radialMps + tHat.z * alongMps,
    };
    const speedMps = Math.hypot(velocity.x, velocity.y, velocity.z);
    simPos.seedAt(world, orbitalBasis(local, velocity));
    syncBubbleFrame();
    syncShipTransform();
    flight.velocity.x = velocity.x;
    flight.velocity.y = velocity.y;
    flight.velocity.z = velocity.z;
    adoptFlightFrame(bodyId);
    aimAlongVelocity(flight);
    _flightQuat.setFromUnitVectors(new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(velocity.x / speedMps, velocity.y / speedMps, velocity.z / speedMps));
    flight.orientation.x = _flightQuat.x;
    flight.orientation.y = _flightQuat.y;
    flight.orientation.z = _flightQuat.z;
    flight.orientation.w = _flightQuat.w;
    ship.group.quaternion.copy(invBubble).multiply(_flightQuat);
    return { ok: true, why: null, bodyId, gm, radiusM: distM, speedMps, periodS: 0 };
  }

  return {
    setFlightTarget,
    pickTarget,
    rebuildTargetOptions,
    guidanceToTarget,
    pilotReadout,
    turnSideFromOffAxis,
    parkInOrbit,
    parkBetweenBodies,
    parkOutwardFrom,
    parkApproachTo,
  };
}
